import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { VoucherResponse } from '@/app/types/voucher'
import { format, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'

export type DebitNoteTableItem = VoucherResponse

// Action cell component that can use hooks
const ActionCell: React.FC<{ debitNote: DebitNoteTableItem }> = ({ debitNote }) => {
  const navigate = useNavigate()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => navigate(`/debit-notes/detail/${debitNote.id}`)}
          className="cursor-pointer"
        >
          <Eye className="mr-2 h-4 w-4" />
          View
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={debitNote.is_cancelled}
          onClick={() => navigate(`/debit-notes/detail/${debitNote.id}`)}
          className="cursor-pointer"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const columns: ColumnDef<DebitNoteTableItem>[] = [
  {
    accessorKey: 'voucher_number',
    header: 'Debit Note #',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.voucher_number}</div>
        {row.original.is_cancelled && (
          <Badge variant="destructive" className="mt-1">
            Cancelled
          </Badge>
        )}
      </div>
    )
  },
  {
    accessorKey: 'party.name',
    header: 'Vendor',
    cell: ({ row }) => (
      <div className="max-w-[200px] truncate" title={row.original.party?.name}>
        {row.original.party?.name || '—'}
      </div>
    )
  },
  {
    accessorKey: 'reference_number',
    header: 'Reference',
    cell: ({ row }) => (
      <div className="max-w-[200px] truncate" title={row.original.reference_number || ''}>
        {row.original.reference_number || '—'}
      </div>
    )
  },
  {
    accessorKey: 'transaction_date',
    header: 'Date',
    cell: ({ row }) => {
      if (!row.original.transaction_date) return '—'
      try {
        return format(
          typeof row.original.transaction_date === 'string'
            ? parseISO(row.original.transaction_date)
            : row.original.transaction_date,
          'MMM dd, yyyy'
        )
      } catch (error) {
        console.error('Date parsing error:', error)
        return 'Invalid date'
      }
    }
  },
  {
    accessorKey: 'total_amount',
    header: 'Amount',
    cell: ({ row }) => {
      const amount = row.original.total_amount
        ? typeof row.original.total_amount === 'string'
          ? parseFloat(row.original.total_amount)
          : row.original.total_amount
        : 0

      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(amount)
    }
  },
  {
    id: 'actions',
    cell: ({ row }) => <ActionCell debitNote={row.original} />
  }
]
